import Image from "next/image";
import { useState } from "react";
import { themeColors } from "@/utils/themeColors";
import { FlexColumn, FlexRowAlignCenter } from "@/components/Flex";
import { ASSETS_URL } from "@/config/deployConstants";
import { EDITOR_CSS_VAR } from "@/components/FullPageEditor/utils/constants";
import CircularRectTextButton, {
    CircularRectTextButtonProps,
} from "@/components/CircularButton/styled/CircularRectTextButton";
import { ConditionalLink } from "@/components/ConditionalLink";
import LazyLoaded from "@/components/LazyLoaded";
import dynamic from "next/dynamic";
import { parseContent } from "@/components/FullPageEditor/utils/contentParser";
import { Editor } from "@knowt/editor/core";

const ImportPopup = dynamic(() => import("@/components/Popup/ImportPopup"));

const driveImg = `${ASSETS_URL}/images/drive.svg`;
const pdfImg = `${ASSETS_URL}/images/pdf-icon.svg`;
const videoImg = `${ASSETS_URL}/images/video-icon.svg`;
const pptImg = `${ASSETS_URL}/images/pptx-icon.svg`;

const TemplateButton = ({
    sx,
    img,
    label,
    href,
    ...props
}: { img: string; label: string; href?: string } & CircularRectTextButtonProps) => {
    return (
        <ConditionalLink condition={!!href} href={href}>
            <CircularRectTextButton
                component={href ? "div" : "button"}
                className="bodyBold2"
                sx={{
                    borderColor: themeColors.neutral1,
                    backgroundColor: "transparent",
                    height: "4rem",
                    padding: "0 2rem",
                    "&:hover": { backgroundColor: themeColors.neutral1 },
                    "&:active": { backgroundColor: themeColors.neutral2 },
                    gap: "1rem",
                    ...sx,
                }}
                {...props}>
                <Image src={img} alt={label + "image"} width="18" height="18" style={{ objectFit: "contain" }} />
                {label}
            </CircularRectTextButton>
        </ConditionalLink>
    );
};

type AITemplateProps = {
    mediaId?: string | null;
    editor: Editor;
};

const CustomPlaceholderUI = ({ editor }: AITemplateProps) => {
    const [importPopupOpen, setImportPopupOpen] = useState(false);

    const importNewNote = async ({ content }: { content: string }) => {
        const result = parseContent(content);
        editor?.commands.setContent(result);
        setImportPopupOpen(false);
    };

    return (
        <FlexColumn
            style={{
                position: "fixed",
                bottom: "4.5rem",
                justifyContent: "flex-end",
                width: "100%",
                padding: `0 ${EDITOR_CSS_VAR.SIDE_PADDING}`,
                backgroundColor: "transparent",
            }}>
            <p className="bodyBold2" style={{ marginBottom: "1.6rem", color: themeColors.neutral2 }}>
                Get started with
            </p>
            <FlexRowAlignCenter style={{ gap: "1.6rem" }}>
                {<TemplateButton label="PDF" img={pdfImg} href={`/ai-pdf-summarizer`} />}
                {<TemplateButton label="PPT" img={pptImg} href={`/ai-powerpoint-summarizer`} />}
                {<TemplateButton label="Video" img={videoImg} href={`/ai-video-summarizer`} />}
                <TemplateButton label="Import from Drive" img={driveImg} onClick={() => setImportPopupOpen(true)} />
            </FlexRowAlignCenter>

            <LazyLoaded load={importPopupOpen}>
                <ImportPopup
                    isOpen={importPopupOpen}
                    close={() => setImportPopupOpen(false)}
                    importNewNote={importNewNote}
                />
            </LazyLoaded>
        </FlexColumn>
    );
};

export default CustomPlaceholderUI;
